import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { API, errMsg } from "@/lib/api";

const EMPTY = { question: "", answer: "", order: 0, active: true };

export default function AdminFaqs() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => API.get("/admin/faqs").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) return toast.error("Question and answer are required");
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (dialog?.id) await API.patch(`/admin/faqs/${dialog.id}`, payload);
      else await API.post("/admin/faqs", payload);
      toast.success("FAQ saved");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const move = async (idx, dir) => {
    const a = items[idx];
    const b = items[idx + dir];
    if (!a || !b) return;
    try {
      await API.patch(`/admin/faqs/${a.id}`, { question: a.question, answer: a.answer, order: b.order, active: !!a.active });
      await API.patch(`/admin/faqs/${b.id}`, { question: b.question, answer: b.answer, order: a.order, active: !!b.active });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-faqs-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl tracking-tight">FAQs</h1>
        <Button className="rounded-full" onClick={() => { setForm({ ...EMPTY, order: (items || []).length + 1 }); setDialog({}); }} data-testid="add-faq-btn">
          <Plus className="mr-2 h-4 w-4" /> Add FAQ
        </Button>
      </div>

      {!items ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-center py-16 border rounded-xl" data-testid="faqs-admin-empty">No FAQs yet.</p>
      ) : (
        <div className="space-y-3" data-testid="faqs-admin-list">
          {items.map((f, i) => (
            <Card key={f.id} data-testid={`admin-faq-${f.id}`}>
              <CardContent className="p-5 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${!f.active ? "line-through text-muted-foreground" : ""}`}>{f.question}</p>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{f.answer}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`faq-up-${f.id}`}><ArrowUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`faq-down-${f.id}`}><ArrowDown className="h-4 w-4" /></Button>
                  <Switch checked={!!f.active} onCheckedChange={async () => {
                    try {
                      await API.patch(`/admin/faqs/${f.id}`, { question: f.question, answer: f.answer, order: f.order || 0, active: !f.active });
                      load();
                    } catch (e) { toast.error(errMsg(e)); }
                  }} data-testid={`faq-active-${f.id}`} aria-label="Visible" />
                  <Button variant="ghost" size="icon" onClick={() => { setForm({ question: f.question, answer: f.answer, order: f.order || 0, active: !!f.active }); setDialog(f); }} data-testid={`edit-faq-${f.id}`} aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={async () => {
                    try {
                      await API.delete(`/admin/faqs/${f.id}`);
                      toast.success("Deleted");
                      load();
                    } catch (e) { toast.error(errMsg(e)); }
                  }} data-testid={`delete-faq-${f.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="faq-dialog">
          <DialogHeader><DialogTitle>{dialog?.id ? "Edit FAQ" : "New FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Question *</Label>
              <Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question-input" />
            </div>
            <div className="space-y-2">
              <Label>Answer *</Label>
              <Textarea rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Visible on website</Label>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="faq-active-switch" />
            </div>
            <Button className="w-full rounded-full" onClick={save} data-testid="save-faq-btn">Save FAQ</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
